import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AccountMovementsModal from './AccountMovementsModal';
import ErrorBoundary from './ErrorBoundary';

const BankAccountsBalance = () => {
  const [accounts, setAccounts] = useState([]);
  const [totalBalance, setTotalBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serviceAvailable, setServiceAvailable] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const fetchBalances = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/bank-accounts/balance`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setAccounts(response.data.accounts);
      setTotalBalance(response.data.total_balance);
      setServiceAvailable(response.data.service_available);
      setLastUpdate(new Date(response.data.timestamp));
    } catch (err) {
      console.error('Error fetching bank balances:', err);
      setError('Error al cargar saldos de cuentas bancarias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalances();
  }, []);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    if (status === 'A' || status === 'active') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Activa</span>;
    } else if (status === 'T') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">No Permite Débito</span>;
    } else if (status === '0') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Controlada</span>;
    } else {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Desconocido</span>;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Cargando saldos...</span>
        </div>
      </div>
    );
  }

  if (error && accounts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{error}</h3>
          <button
            onClick={fetchBalances}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 mb-6">
      {/* Header con botón de actualizar */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Saldos de Cuentas Bancarias</h2>
            {lastUpdate && (
              <p className="text-sm text-gray-500 mt-1">
                Última actualización: {lastUpdate.toLocaleString('es-VE')}
              </p>
            )}
          </div>
          <button
            onClick={fetchBalances}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className={`h-5 w-5 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Actualizar
          </button>
        </div>

        {/* Alerta si el servicio no está disponible */}
        {!serviceAvailable && (
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Servicio no activado:</strong> Los servicios de consulta de cuentas de Banco Plaza no están activos. 
                  Contacta al soporte de Banco Plaza para habilitar los permisos READ en tu API key.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Total Balance */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 mb-6">
          <div className="text-white">
            <p className="text-sm font-medium opacity-90">Balance Total</p>
            <p className="text-4xl font-bold mt-2">{formatCurrency(totalBalance)}</p>
            <p className="text-sm opacity-75 mt-1">Suma de todas las cuentas</p>
          </div>
        </div>

        {/* Individual Accounts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {accounts.map((account, index) => (
            <div
              key={index}
              onClick={() => !account.error && setSelectedAccount(account)}
              data-testid={`bank-account-card-${account.account_number}`}
              className={`border rounded-lg p-6 transition-all ${
                account.error
                  ? 'border-red-200 bg-red-50'
                  : 'border-gray-200 bg-white cursor-pointer hover:border-blue-400 hover:shadow-md'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{account.name}</h3>
                  <p className="text-sm text-gray-500 font-mono">{account.account_number}</p>
                  {account.rif && (
                    <p className="text-xs text-gray-400 font-mono">RIF: {account.rif}</p>
                  )}
                  {account.owner_type === 'merchant' && (
                    <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded bg-orange-100 text-orange-800">CONCENTRADORA PROPIA</span>
                  )}
                </div>
                {getStatusBadge(account.status)}
              </div>

              {account.error ? (
                <div className="mt-4">
                  <div className="flex items-start space-x-2">
                    <svg className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm text-red-700 font-medium">Servicio no disponible</p>
                      <p className="text-xs text-red-600 mt-1">{account.error}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mt-4">
                    <p className="text-3xl font-bold text-gray-900">
                      {formatCurrency(account.balance)}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">{account.currency}</p>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Tipo de Cuenta</p>
                        <p className="font-medium text-gray-900">{account.account_type}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Fecha Apertura</p>
                        <p className="font-medium text-gray-900">{account.opening_date}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 text-[11px] text-blue-600 font-medium flex items-center gap-1">
                    Click para ver movimientos →
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {selectedAccount && (
        <ErrorBoundary
          fallbackTitle={`Error mostrando movimientos de ${selectedAccount.name}`}
          onError={() => { /* dejar el modal montado para que el usuario pueda cerrarlo */ }}
        >
          <AccountMovementsModal
            account={selectedAccount.account_number}
            accountLabel={selectedAccount.name}
            idCliente={
              selectedAccount.owner_type === 'merchant' && selectedAccount.rif
                ? String(selectedAccount.rif).replace(/-/g, '').toUpperCase()
                : null
            }
            onClose={() => setSelectedAccount(null)}
          />
        </ErrorBoundary>
      )}
    </div>
  );
};

export default BankAccountsBalance;
